import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react';
import {platform, env} from '../../platform';

export const baseUrl = () =>
  `${env('REACT_APP_API_ENDPOINT', 'https://api.tpsportal.co.nz')}/api/`;

/**
 * BookMe tracker API. Resolved through the platform adapter like `baseUrl`,
 * so it is safe to call at module scope once the adapter is registered.
 */
export const bookMeBaseUrl = () =>
  `${env(
    'REACT_APP_BOOKME_API_BASE_URL',
    'https://tracker-api.tpsportal.co.nz'
  )}/api/`;

/**
 * Shared RTK Query API for both apps.
 *
 * Everything platform-specific is reached through the platform adapter:
 *
 *  - credentials: web sends its cookie jar alongside the bearer token, native
 *    has no cookie jar at all (`omit`).
 *  - token lookup: web reads a cookie, native reads the secure enclave. Both
 *    are async here, which `prepareHeaders` supports natively.
 *  - the invalid-credentials escape hatch: web does a hard location change,
 *    native resets the navigation stack to the root route.
 */
// Built on first request rather than at module load: the platform adapter is
// registered during bootstrap, which happens after this module is imported.
// eslint-disable-next-line fp/no-let
let baseQuery = null;

const getBaseQuery = () => {
  if (baseQuery) {
    return baseQuery;
  }

  // eslint-disable-next-line fp/no-mutation
  baseQuery = fetchBaseQuery({
    baseUrl: baseUrl(),
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: platform().fetchCredentials,
    prepareHeaders: async headers => {
      const {auth, navigation} = platform();
      const [token, authenticated] = await Promise.all([
        auth.getToken(),
        auth.isAuthenticated(),
      ]);

      if (token && authenticated) {
        headers.set('Authorization', `Bearer ${token}`);
      } else if (!token && authenticated) {
        // Flagged as authenticated but the token is gone — the session is
        // unrecoverable. Drop the flag and bounce to the root.
        await auth.clear();
        navigation.toRoot();
      }

      return headers;
    },
  });

  return baseQuery;
};

const api = createApi({
  baseQuery: (args, apiArgs, extraOptions) =>
    getBaseQuery()(args, apiArgs, extraOptions),
  endpoints: () => ({}),
  tagTypes: [
    'AGREEMENTS',
    'AGREEMENT_DETAILS',
    'APPLICATIONS',
    'APPLICATION_DETAILS',
    'PROPERTY_LIST',
    'PROPERTIES_CHECK',
    'TWO_FACTOR',
  ],
});

export default api;
